import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { textToItems, breakLines, layoutParagraph, LayoutParams } from '../../pdf/book/layout';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

export class TestParagraph implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    const page = pdf.addPage();
    const font = new PdfFont(PdfFont.FONT_NAME, 12);
    const titleFont = new PdfFont(PdfFont.FONT_NAME, 20);

    const titleRect = new PdfRect(MARGIN, MM(5), PdfSize.A4.width - 2 * MARGIN, MM(10));
    page.drawString(
      'Test Knuth-Plass Paragraph Layout',
      titleFont,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    let yPos = 25;

    const testTexts = [
      'This is a short line.',
      'This is a longer line that should demonstrate the algorithm working correctly.',
      'The quick brown fox jumps over the lazy dog. This is a classic pangram used for testing text formatting.',
      'Supercalifragilisticexpialidocious is a very long word that might need hyphenation when the line is narrow.',
    ];

    for (const text of testTexts) {
      const rect = new PdfRect(MARGIN, MM(yPos), PdfSize.A4.width - 2 * MARGIN, MM(5));
      page.drawString(
        `Testing: "${text.substring(0, 40)}..."`,
        font,
        PdfBrush.LightGray,
        null,
        rect,
        ETextAlign.CenterLeft
      );
      yPos += 8;

      const layoutParams: LayoutParams = {
        font,
        lineWidth: PdfSize.A4.width - 2 * MARGIN,
        tolerance: 2,
      };

      const result = layoutParagraph(text, layoutParams);

      page.drawString(
        `Breaks found: ${result.breaks.length}`,
        font,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, MM(yPos), MM(50), MM(5)),
        ETextAlign.CenterLeft
      );
      yPos += 8;

      let lineY = yPos;
      for (const breakPoint of result.breaks) {
        const lineText = extractLineText(result.items, breakPoint);
        const lineRect = new PdfRect(
          MARGIN + 5,
          MM(lineY),
          PdfSize.A4.width - 2 * MARGIN - 10,
          MM(5)
        );
        page.drawString(lineText, font, PdfBrush.Black, null, lineRect, ETextAlign.CenterLeft);
        lineY += 6;
      }

      yPos = lineY + 15;
    }

    const longText =
      'This is a test of the paragraph layout algorithm. It should break the text into multiple lines based on the available width. The Knuth-Plass algorithm is used to determine optimal line breaks. This implementation demonstrates professional typesetting quality.';

    const sectionTitleRect = new PdfRect(MARGIN, MM(yPos), PdfSize.A4.width - 2 * MARGIN, MM(8));
    page.drawString(
      'Full Paragraph Test:',
      font,
      PdfBrush.Black,
      null,
      sectionTitleRect,
      ETextAlign.CenterLeft
    );
    yPos += 12;

    const layoutParams: LayoutParams = {
      font,
      lineWidth: PdfSize.A4.width - 2 * MARGIN - 20,
      tolerance: 2,
    };

    const result = layoutParagraph(longText, layoutParams);

    if (result.breaks.length === 0) {
      const directRect = new PdfRect(
        MARGIN + 10,
        MM(yPos),
        PdfSize.A4.width - 2 * MARGIN - 20,
        MM(40)
      );
      page.drawString(longText, font, PdfBrush.Black, null, directRect, ETextAlign.CenterLeft);
    } else {
      let lineY = yPos;
      for (const breakPoint of result.breaks) {
        const lineText = extractLineText(result.items, breakPoint);
        if (lineText) {
          const lineRect = new PdfRect(
            MARGIN + 10,
            MM(lineY),
            PdfSize.A4.width - 2 * MARGIN - 20,
            MM(5)
          );
          page.drawString(lineText, font, PdfBrush.Black, null, lineRect, ETextAlign.CenterLeft);
          lineY += 6;
        }
      }
    }
  }
}

function extractLineText(items: any[], breakPoint: any): string {
  let text = '';
  let pos = 0;

  for (const item of items) {
    if (pos >= breakPoint.position) break;

    if (item.type === 'box' && item.content) {
      text += item.content;
    } else if (item.type === 'glue') {
      text += ' ';
    }
    pos++;
  }

  return text.trim();
}
